import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { CheckRounded, CloseRounded } from "@mui/icons-material";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { useFirestore } from "reactfire";
import * as XLSX from "xlsx";
import { useFeedback } from "../../../providers/feedback/feedback";

type Submission = {
  docId: string;
  monthKey: string;
  deanery: string;
  parish: string;
  organization: "ICYM" | "YCS";
  dateOfConduction: string;
  youcatInchargeName: string;
  email: string;
  contact: string;
  youthsParticipatedCount: number;
  answers?: Record<string, string>;
  imageUrls?: string[];
  approvalStatus?: "pending" | "approved" | "rejected";
  reviewedAt?: string;
};

const SUBMISSIONS_COLLECTION = "youcatMonthlyUpdateSubmissions";

function getMonthLabel(monthKey: string) {
  const [year, month] = monthKey.split("-").map(Number);

  if (!year || !month) {
    return monthKey;
  }

  return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function getCurrentIstMonthKey() {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
  });
  const parts = formatter.formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  return `${year}-${month}`;
}

function downloadWorkbook(submissions: Submission[], organization: "ICYM" | "YCS") {
  const filtered = submissions.filter((item) => item.organization === organization);
  const grouped = filtered.reduce<Record<string, Submission[]>>((acc, item) => {
    const key = item.monthKey || "Unknown";
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});

  const workbook = XLSX.utils.book_new();
  Object.keys(grouped)
    .sort((a, b) => b.localeCompare(a))
    .forEach((monthKey) => {
      const rows = grouped[monthKey].map((item) => ({
        "Date of Conduction": item.dateOfConduction,
        Deanery: item.deanery,
        Parish: item.parish,
        "YouCat In-charge": item.youcatInchargeName,
        Email: item.email,
        Contact: item.contact,
        "Unit Type": item.organization,
        "Youths Participated Count": item.youthsParticipatedCount,
        "Image Count": item.imageUrls?.length || 0,
        ...(item.answers || {}),
      }));
      const sheet = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(workbook, sheet, getMonthLabel(monthKey));
    });

  XLSX.writeFile(workbook, `YouCat Updates ${organization}.xlsx`);
}

export default function YoucatResponsesTab() {
  const firestore = useFirestore();
  const { showSnackbar } = useFeedback();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingSubmissionId, setUpdatingSubmissionId] = useState("");
  const currentMonthKey = useMemo(() => getCurrentIstMonthKey(), []);

  useEffect(() => {
    let isMounted = true;

    async function loadSubmissions() {
      try {
        const snapshot = await getDocs(
          query(collection(firestore, SUBMISSIONS_COLLECTION), orderBy("createdAt", "desc")),
        );
        if (!isMounted) {
          return;
        }
        setSubmissions(
          snapshot.docs.map((submissionDoc) => ({
            docId: submissionDoc.id,
            ...(submissionDoc.data() as Omit<Submission, "docId">),
          })),
        );
      } catch (error) {
        console.error("Failed to load YouCat submissions", error);
        showSnackbar("Failed to load YouCat submissions");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadSubmissions();

    return () => {
      isMounted = false;
    };
  }, [firestore, showSnackbar]);

  const icymCount = useMemo(
    () => submissions.filter((item) => item.organization === "ICYM").length,
    [submissions],
  );
  const ycsCount = useMemo(
    () => submissions.filter((item) => item.organization === "YCS").length,
    [submissions],
  );
  const pendingSubmissions = useMemo(
    () => submissions.filter((item) => item.approvalStatus === "pending"),
    [submissions],
  );
  const approvedSubmissions = useMemo(
    () => submissions.filter((item) => item.approvalStatus === "approved"),
    [submissions],
  );
  const rejectedSubmissions = useMemo(
    () =>
      submissions.filter(
        (item) =>
          item.approvalStatus === "rejected" && item.monthKey === currentMonthKey,
      ),
    [currentMonthKey, submissions],
  );

  async function handleReviewSubmission(
    submissionId: string,
    approvalStatus: "approved" | "rejected",
  ) {
    try {
      setUpdatingSubmissionId(submissionId);
      await updateDoc(doc(firestore, SUBMISSIONS_COLLECTION, submissionId), {
        approvalStatus,
        reviewedAt: new Date().toISOString(),
      });

      setSubmissions((prev) =>
        prev.map((item) =>
          item.docId === submissionId
            ? {
                ...item,
                approvalStatus,
                reviewedAt: new Date().toISOString(),
              }
            : item,
        ),
      );
      showSnackbar(
        approvalStatus === "approved"
          ? "Submission approved and visible on the website"
          : "Submission rejected and kept in storage",
      );
    } catch (error) {
      console.error("Failed to review YouCat submission", error);
      showSnackbar("Failed to update submission status");
    } finally {
      setUpdatingSubmissionId("");
    }
  }

  function getStatusColor(status: Submission["approvalStatus"]) {
    if (status === "approved") {
      return "success";
    }

    if (status === "rejected") {
      return "error";
    }

    return "warning";
  }

  function getStatusLabel(status: Submission["approvalStatus"]) {
    if (status === "approved") {
      return "Approved";
    }

    if (status === "rejected") {
      return "Rejected";
    }

    return "Pending Approval";
  }

  return (
    <Box p={3}>
      <Typography variant="h6" fontWeight={600} mb={1}>
        Responses, Gallery, and Excel
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Download separate Excel files for ICYM and YCS. Each workbook uses one
        sheet per month in the format Month Year.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Monthly update images are always stored. Use the tick to publish a
        submission on the public YouCat page or the X to keep it hidden.
      </Typography>

      <Box display="flex" gap={2} flexWrap="wrap" mb={4}>
        <Button
          variant="contained"
          onClick={() => downloadWorkbook(submissions, "ICYM")}
          disabled={!icymCount}
        >
          Download ICYM Excel
        </Button>
        <Button
          variant="contained"
          onClick={() => downloadWorkbook(submissions, "YCS")}
          disabled={!ycsCount}
        >
          Download YCS Excel
        </Button>
      </Box>

      {isLoading ? (
        <Typography>Loading YouCat responses...</Typography>
      ) : !submissions.length ? (
        <Typography color="text.secondary">
          No monthly update responses submitted yet.
        </Typography>
      ) : (
        <Stack spacing={4}>
          <Box>
            <Typography variant="h6" fontWeight={700} mb={0.5}>
              Pending Section
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              New submissions stay here until approved.
            </Typography>
            {!pendingSubmissions.length ? (
              <Typography color="text.secondary">No pending submissions.</Typography>
            ) : (
              <Grid container spacing={3}>
                {pendingSubmissions.map((item) => (
                  <Grid item xs={12} md={6} lg={4} key={item.docId}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="overline" color="warning.main" fontWeight={800}>
                          {getMonthLabel(item.monthKey)}
                        </Typography>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1}
                          mt={1}
                        >
                          <Chip
                            size="small"
                            color={getStatusColor(item.approvalStatus)}
                            label={getStatusLabel(item.approvalStatus)}
                          />
                          <Box>
                            <IconButton
                              color="success"
                              onClick={() => handleReviewSubmission(item.docId, "approved")}
                              disabled={updatingSubmissionId === item.docId}
                            >
                              <CheckRounded />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleReviewSubmission(item.docId, "rejected")}
                              disabled={updatingSubmissionId === item.docId}
                            >
                              <CloseRounded />
                            </IconButton>
                          </Box>
                        </Stack>
                        <Typography variant="h6" fontWeight={700}>
                          {item.parish}
                        </Typography>
                        <Typography fontWeight={600}>{item.deanery}</Typography>
                        <Typography color="text.secondary" mt={1}>
                          {item.organization} | {item.youthsParticipatedCount} youth
                        </Typography>
                        <Typography color="text.secondary" mt={1}>
                          In-charge: {item.youcatInchargeName}
                        </Typography>
                        <Typography color="text.secondary" mt={1}>
                          Images: {item.imageUrls?.length || 0}
                        </Typography>
                        {item.imageUrls?.[0] ? (
                          <Box
                            component="img"
                            src={item.imageUrls[0]}
                            alt={item.parish}
                            sx={{
                              width: "100%",
                              height: 180,
                              objectFit: "contain",
                              mt: 2,
                              borderRadius: 2,
                              background:
                                "linear-gradient(180deg, rgba(255,249,235,1) 0%, rgba(255,255,255,1) 100%)",
                              p: 1,
                            }}
                          />
                        ) : null}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          <Box>
            <Typography variant="h6" fontWeight={700} mb={0.5}>
              Approved Section
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Once approved, submissions move here and become visible on the public YouCat page.
            </Typography>
            {!approvedSubmissions.length ? (
              <Typography color="text.secondary">No approved submissions yet.</Typography>
            ) : (
              <Grid container spacing={3}>
                {approvedSubmissions.map((item) => (
                  <Grid item xs={12} md={6} lg={4} key={item.docId}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="overline" color="warning.main" fontWeight={800}>
                          {getMonthLabel(item.monthKey)}
                        </Typography>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1}
                          mt={1}
                        >
                          <Chip
                            size="small"
                            color={getStatusColor(item.approvalStatus)}
                            label={getStatusLabel(item.approvalStatus)}
                          />
                          <IconButton
                            color="error"
                            onClick={() => handleReviewSubmission(item.docId, "rejected")}
                            disabled={updatingSubmissionId === item.docId}
                          >
                            <CloseRounded />
                          </IconButton>
                        </Stack>
                        <Typography variant="h6" fontWeight={700}>
                          {item.parish}
                        </Typography>
                        <Typography fontWeight={600}>{item.deanery}</Typography>
                        <Typography color="text.secondary" mt={1}>
                          {item.organization} | {item.youthsParticipatedCount} youth
                        </Typography>
                        <Typography color="text.secondary" mt={1}>
                          In-charge: {item.youcatInchargeName}
                        </Typography>
                        <Typography color="text.secondary" mt={1}>
                          Images: {item.imageUrls?.length || 0}
                        </Typography>
                        {item.imageUrls?.[0] ? (
                          <Box
                            component="img"
                            src={item.imageUrls[0]}
                            alt={item.parish}
                            sx={{
                              width: "100%",
                              height: 180,
                              objectFit: "contain",
                              mt: 2,
                              borderRadius: 2,
                              background:
                                "linear-gradient(180deg, rgba(255,249,235,1) 0%, rgba(255,255,255,1) 100%)",
                              p: 1,
                            }}
                          />
                        ) : null}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          <Box>
            <Typography variant="h6" fontWeight={700} mb={0.5}>
              Rejected Section
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Rejected submissions stay visible here only for the current month
              so they can be corrected and resubmitted if needed.
            </Typography>
            {!rejectedSubmissions.length ? (
              <Typography color="text.secondary">
                No rejected submissions for the current month.
              </Typography>
            ) : (
              <Grid container spacing={3}>
                {rejectedSubmissions.map((item) => (
                  <Grid item xs={12} md={6} lg={4} key={item.docId}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="overline" color="warning.main" fontWeight={800}>
                          {getMonthLabel(item.monthKey)}
                        </Typography>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1}
                          mt={1}
                        >
                          <Chip
                            size="small"
                            color={getStatusColor(item.approvalStatus)}
                            label={getStatusLabel(item.approvalStatus)}
                          />
                          <IconButton
                            color="success"
                            onClick={() => handleReviewSubmission(item.docId, "approved")}
                            disabled={updatingSubmissionId === item.docId}
                          >
                            <CheckRounded />
                          </IconButton>
                        </Stack>
                        <Typography variant="h6" fontWeight={700}>
                          {item.parish}
                        </Typography>
                        <Typography fontWeight={600}>{item.deanery}</Typography>
                        <Typography color="text.secondary" mt={1}>
                          {item.organization} | {item.youthsParticipatedCount} youth
                        </Typography>
                        <Typography color="text.secondary" mt={1}>
                          In-charge: {item.youcatInchargeName}
                        </Typography>
                        <Typography color="text.secondary" mt={1}>
                          Images: {item.imageUrls?.length || 0}
                        </Typography>
                        {item.imageUrls?.[0] ? (
                          <Box
                            component="img"
                            src={item.imageUrls[0]}
                            alt={item.parish}
                            sx={{
                              width: "100%",
                              height: 180,
                              objectFit: "contain",
                              mt: 2,
                              borderRadius: 2,
                              background:
                                "linear-gradient(180deg, rgba(255,249,235,1) 0%, rgba(255,255,255,1) 100%)",
                              p: 1,
                            }}
                          />
                        ) : null}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}
